export const ZONE_COLORS = [
  "#FF4444", "#00BBFF", "#FFCC00", "#FF00FF", "#00FF88",
  "#FF8800", "#8855FF", "#00FFFF", "#FF0066", "#88FF00",
  "#FF6633", "#3399FF", "#FFFF00", "#CC44FF", "#33FF99",
  "#FF9900", "#6644FF", "#44FFEE", "#FF3388", "#AAFF33",
];

export const POLY_FILL_OPACITY = 0.45;
export const POLY_FILL_OPACITY_HOVER = 0.65;
export const POLY_STROKE_OPACITY = 1.0;
export const POLY_STROKE_WEIGHT = 3;

export const DEPTH_PRESETS = [2, 3, 4, 6, 9, 12];
export const DEFAULT_DEPTH = 3;
export const MAX_ZONES = 100;

// Auto-assigned zone color. For the first ZONE_COLORS.length zones we use the
// hand-picked palette above (high contrast, known-good on satellite imagery).
// Beyond that we generate evenly-spaced hues so every zone stays visually
// distinct on large sites instead of wrapping back to palette[0]. Reps can
// still override any zone's color manually via the per-zone picker.
//
// IMPORTANT: returns a 7-char hex string (#RRGGBB), never hsl(). The
// mulch_zone.color DB column is varchar(7); an hsl() string overflows it and
// makes the save fail with a 500. Generating hex keeps colors unique past the
// palette without needing a column migration.
export function zoneColorForIndex(index: number): string {
  if (index < ZONE_COLORS.length) return ZONE_COLORS[index];
  // Golden-angle hue stepping spreads successive hues far apart so adjacent
  // zones never look alike, even for hundreds of zones.
  const hue = ((index * 137.508) % 360) / 360;
  return hslToHex(hue, 0.85, 0.55);
}

// HSL (0..1 each) -> #RRGGBB. Kept local so zoneColorForIndex always yields a
// value that fits the varchar(7) color column.
function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

const SQFT_PER_SQ_METER = 10.7639104167;
const SQFT_PER_CUBIC_YARD_PER_INCH = 324;

export function sqMetersToSqFeet(sqm: number): number {
  return sqm * SQFT_PER_SQ_METER;
}

export function calcCubicYards(sqFeet: number, depthInches: number): number {
  return (sqFeet * depthInches) / SQFT_PER_CUBIC_YARD_PER_INCH;
}

export function formatNumber(num: number, decimals = 0): string {
  if (!isFinite(num)) return "0";
  if (num === 0) return "0";
  if (num < 1 && decimals === 0) return num.toFixed(1);
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatCurrency(num: number): string {
  if (!isFinite(num)) return "$0";
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}
